import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Table from "../../../core/common/dataTable/index";
import PredefinedDateRanges from "../../../core/common/datePicker";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import "../employees/index.css";
import { Moment } from "moment";
import { getBookData } from "../../../apis/auth";
import { formatDate } from "../../../environment";
import { getEvAccountingRows } from "./evAccounting";

const BankList = () => {


  const [allEmployeeData, setEmployeeData] = useState<EmployeeData[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [loadError, setLoadError] = useState<string>("");
  const [state, setState] = useState<{
    start: Moment | undefined;
    end: Moment | undefined;
  }>({
    start: undefined,
    end: undefined,
  });
  const { start, end } = state;

  type EmployeeResponse = {
    title: string;
    CurrentRole: string;
    amount: number;
    description: string;
    createdAt: string;
    id: any;
    Status: string;
    cash_bank: string;
  };

  type EmployeeData = {
    key: number;
    date: any;
    Name: string;
    id: any;
    amount: number;
    address: string;
    cash_bank: string;
    type: string;
    direction: string;
  };
  const loginData = sessionStorage.getItem("info");
  const BankData = useCallback(async () => {
    try {
      setLoadError("");
      const data = {
        start_date: start ? start.format("MM/DD/YYYY") : undefined,
        end_date: end ? end.format("MM/DD/YYYY") : undefined,
        user_id: JSON.parse(loginData).user_id,
        cash_bank: "cash"
      };
      const [bookResult, evResult] = await Promise.allSettled([
        getBookData(data),
        getEvAccountingRows("cash", start ? start.format("YYYY-MM-DD") : "", end ? end.format("YYYY-MM-DD") : ""),
      ]);
      if (bookResult.status === "rejected") throw bookResult.reason;
      const response = bookResult.value;
      const evRows = evResult.status === "fulfilled" ? evResult.value : [];
      if (evResult.status === "rejected") setLoadError(`EV loan accounting could not be loaded: ${evResult.reason?.message || "Request failed"}`);
      const expenseRows = response?.data?.expenses?.map(
        (item: EmployeeResponse, i: number) => ({
          key: `${i + 1}`,
          date: item.createdAt || "Not Assigned",
          Name: `${item.title}`,
          amount: item.amount,
          address: item.description || "Not Assigned",
          id: item.id,
          cash_bank: item.cash_bank,
          type: "Expense",
          direction: "Debit",
        })
      );
      const employeeData = [...(expenseRows || []), ...evRows].map((item, index) => ({ ...item, key: index + 1 }));
      setEmployeeData(employeeData);
    } catch (error) {
      setLoadError(error?.response?.data?.message || error?.message || "Accounting data could not be loaded");
      console.error("Error fetching employees:", error);
    }
  }, [loginData, start, end]);

  useEffect(() => {
    BankData();
  }, [BankData]);

  // Search across every visible field, not just name/address.
  const filteredEmployeeData = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return allEmployeeData;

    return allEmployeeData.filter((item) =>
      [item.Name, item.address, item.cash_bank, item.amount, item.date, item.type, item.direction].some(
        (value) => String(value ?? "").toLowerCase().includes(query)
      )
    );
  }, [allEmployeeData, searchQuery]);

  const totalAmount = useMemo(
    () =>
      filteredEmployeeData.reduce((sum, item) => sum + (Number(item.amount) || 0), 0),
    [filteredEmployeeData]
  );

  const columns = [
    {
      title: "Sr. No.",
      dataIndex: "key",
    },
    {
      title: "Date",
      dataIndex: "date",
      render: (text: string) => (
        <div className="ms-2">
          <p className="text-dark mb-0">
            <div>{formatDate(text)}</div>
          </p>
        </div>
      ),
    },
    {
      title: "Title",
      dataIndex: "Name",
      render: (_: any, record: EmployeeData) => (
        <div className="d-flex align-items-center">
          <div className="ms-2">
            <p className="text-dark mb-0">
              <div>{record?.Name}</div>
            </p>
          </div>
        </div>
      ),
      sorter: (a: EmployeeData, b: EmployeeData) =>
        a.Name.localeCompare(b.Name),
    },
    { title: "Type", dataIndex: "type" },
    { title: "Direction", dataIndex: "direction" },


    {
      title: "Description",
      dataIndex: "address",
      render: (text: string) => (
        <div className="ms-2">
          <p className="text-dark mb-0">
            <div>{text}</div>
          </p>
        </div>
      ),
    },
    {
      title: "Cash Or Bank",
      dataIndex: "cash_bank",
      render: (text: string) => (
        <div className="ms-2">
          <p className="text-dark mb-0">
            <div>{text}</div>
          </p>
        </div>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      render: (_: any, record: EmployeeData) => (
        <div className="d-flex align-items-center">
          <div className="ms-2">
            <p className="text-dark mb-0">
              <div>{record?.amount}</div>
            </p>
          </div>
        </div>
      ),
    },
  ];

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };


  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2"></div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
              <div className="me-2 mb-2">
              </div>
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>

          <div className="card">
            {loadError && <div className="alert alert-danger m-3" role="alert">{loadError}</div>}
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                <div className="me-3">
                  <div>
                    <label style={{ color: "#000" }}>Search</label>
                    <input
                      type="text"
                      className="form-control form-control-sm me-2"
                      placeholder="Search by Name\Mobile"
                      style={{
                        backgroundColor: "#b4b4b447",
                        padding: "0.2rem 0.85rem",
                        marginTop: "5px",
                      }}
                      value={searchQuery}
                      onChange={handleSearchChange}
                    />
                  </div>
                </div>

                <div className="input-icon-end position-relative">
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "5px",
                    }}
                  >
                    <label style={{ color: "#000" }}>Date Range</label>
                    <PredefinedDateRanges state={state} setState={setState} />
                  </div>
                </div>
                <div style={{ margin: "0 1.5rem" }}>
                  <label style={{ color: "#000" }}>Total Amount</label>
                  <div
                    style={{
                      background: "linear-gradient(135deg, #4CAF50, #2E7D32)",
                      color: "#fff",
                      borderRadius: "8px",
                      padding: "0.35rem 1rem",
                      marginTop: "5px",
                      fontWeight: 700,
                      fontSize: "14px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    ₹ {totalAmount.toLocaleString("en-IN")}
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    margin: "20px 0px 0px 3rem",
                  }}
                >
                  <div className="dropdown me-3">
                    <Link
                      style={{
                        background: "#3ab54bbf",
                        color: "#fff",
                      }}
                      to="#"
                      className="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                      data-bs-toggle="dropdown"
                    >
                      Export
                    </Link>

                    <ul className="dropdown-menu  dropdown-menu-end p-3">
                      <li>
                        <Link to="#" className="dropdown-item rounded-1">
                          <i className="ti ti-file-type-pdf me-1" />
                          Export as PDF
                        </Link>
                      </li>
                      <li>
                        <Link to="#" className="dropdown-item rounded-1">
                          <i className="ti ti-file-type-xls me-1" />
                          Export as Excel{" "}
                        </Link>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            <div className="card-body p-0">
              {filteredEmployeeData && filteredEmployeeData.length > 0 && (
                <Table
                  dataSource={filteredEmployeeData}
                  columns={columns}
                  Selection={true}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BankList;